import { inject, NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './guards/auth.guard';
import { AutoLoginGuard } from './guards/auto-login.guard';

import { DashboardComponent } from './components/dashboard/dashboard.component';
import { OrdiniPMComponent } from './components/ordini-pm/ordini-pm.component';
import { LoginComponent } from './components/login/login.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { AccessoFunzionariListComponent } from './components/accesso-funzionari/accesso-funzionari-list/accesso-funzionari-list.component';
import { BaseAgenteComponent } from './components/shared/base-agente/base-agente.component';
import { ResetPasswordComponent } from './components/reset-password/reset-password.component';
// v2.13.0 W3: rimosso import orfano `ProfileComponent` — era importato
// qui ma nessuna route lo usava. `ProfileComponent` è un modal password
// (selector app-profile) usato SOLO da manufacturer-list, che è
// postponed indefinitely (Phase 3+). Restyle skippato in Sprint 1 W3
// dopo verifica raggiungibilità. Vedi memoria [[diakonix-restyle-leftover-status]].
import { AssegnaOrdineDettagliComponent } from './components/assegna-ordine/assegna-ordine-dettagli/assegna-ordine-dettagli.component';
import { AssegnaOrdineListComponent } from './components/assegna-ordine/assegna-ordine-list/assegna-ordine-list.component';
// Legacy messaggi-list / -dettagli / -new components were removed in
// v2.3.0 (consolidated into ChatComponent). See routes below for the
// backward-compat redirects.
import { GestioneProdottiListComponent } from './components/gestione-prodotti/gestione-prodotti-list/gestione-prodotti-list.component';
import { GestioneProdottiDettagliComponent } from './components/gestione-prodotti/gestione-prodotti-dettagli/gestione-prodotti-dettagli.component';
import { GestioneAccountListComponent } from './components/gestione-account/gestione-account-list/gestione-account-list.component';
// Legacy gestione-account-dettagli / -new / -edit components removed in
// v2.3.0 — replaced by the single-page restyle of GestioneAccountList.
// See routes below for the backward-compat redirects.
import { StatusSpedizioniComponent } from './components/status-spedizioni/status-spedizioni.component';
import { StoricoOrdiniComponent } from './components/storico-ordini/storico-ordini.component';
import { StatisticheComponent } from './components/statistiche/statistiche.component';
import { ControlloQualityComponent } from './components/controllo-quality/controllo-quality.component';
import { StoricoOrdiniDettagliComponent } from './components/storico-ordini/storico-ordini-dettagli/storico-ordini-dettagli.component';
import { PreparazioneOrdiniComponent } from './components/preparazione-ordini/preparazione-ordini.component';
import { PreparazioneOrdiniDetaggliComponent } from './components/preparazione-ordini/preparazione-ordini-detaggli/preparazione-ordini-detaggli.component';
import { CollectionComponent } from './components/preparazione-ordini/collection/collection.component';

import { InfoAgenteComponent } from './components/accesso-funzionari/info-agente/info-agente.component';
import { ScreenLoginGuard } from './guards/screen.guard';
import { ScreenTokenGuard } from './guards/screen-token.guard';
import { WarehouseOrdersComponent } from './components/preparazione-ordini/prepare-order/warehouse-orders/warehouse-orders.component';
import { OrdiniDaFirmareComponent } from './components/ordini-da-firmare/ordini-da-firmare.component';
import { ProductDetailComponent } from './components/gestione-prodotti/product-detail/product-detail.component';
import { ConfirmProductsComponent } from './components/preparazione-ordini/preparazione-ordini-detaggli/confirm-products/confirm-products.component';
import { SettingsComponent } from './components/settings/settings.component';
import { AssegnaValoreComponent } from './components/settings/assegna-valore/assegna-valore.component';
import { BorderòComponent } from './components/borderò/borderò.component';
import { BorderòDettagliComponent } from './components/borderò/borderò-dettagli/borderò-dettagli.component';
import { OrdiniParzialiListComponent } from './components/ordini-parziali/ordini-parziali-list/ordini-parziali-list.component';
import { OrdiniParzialiDettagliComponent } from './components/ordini-parziali/ordini-parziali-dettagli/ordini-parziali-dettagli.component';
import { OrderscmDetComponent } from './components/shared/base-orderscm-list/orderscm-det/orderscm-det.component';
import { ErrorComponent } from '@progress/kendo-angular-inputs';
import { ChatComponent } from './components/messaggi/chat/chat.component';
import { StoricoParzialiComponent } from './components/storico-parziali/storico-parziali.component';
import { AgenteListComponent } from './components/accesso-funzionari/agente-list/agente-list.component';
import { FiestaComponent } from './components/shared/fiesta/fiesta.component';
import { searchTextGuardGuard } from './guards/search-text-guard.guard';
import { ClientListComponent } from './components/accesso-funzionari/client-list/client-list.component';
import { AdminUserGuard } from './guards/admin-user.guard';
import { Helpers } from './models/helpers';
import { ManufacturerListComponent } from './components/manufacturer/manufacturer-list/manufacturer-list.component';
import { CheckOrderManufacturerComponent } from './components/manufacturer/check-order-manufacturer/check-order-manufacturer.component';
import { HistoryOrderDetailComponent } from './components/manufacturer/history-order-detail/history-order-detail.component';
import { FornitoreListAccountsComponent } from './components/fornitore-list-accounts/fornitore-list-accounts.component';
import { FornitoriOrdiniListComponent } from './components/fornitori-ordini-list/fornitori-ordini-list.component';
import { FornitoriOrdiniDettagliComponent } from './components/fornitori-ordini-dettagli/fornitori-ordini-dettagli.component';
import { TracciabilitaFornitoriComponent } from './components/tracciabilita-fornitori/tracciabilita-fornitori.component';
import { FornitoriTrackingComponent } from './components/fornitori-tracking/fornitori-tracking.component';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },

  {
    path: 'dashboard',
    canActivate: [AuthGuard],
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    children: [
      {
        path: '',
        component: DashboardComponent,
      },
    ],
  },
  {
    path: 'dashboard',
    canActivate: [AuthGuard],
    canMatch: [
      () => inject(AdminUserGuard).canMatch([Helpers.PermisionManufacturer]),
    ],
    children: [
      {
        path: '',
        component: ManufacturerListComponent,
      },
      {
        path: ':id',
        pathMatch: 'full',
        redirectTo: ':id/check-order-manufacturer',
      },
      {
        path: ':id/check-order-manufacturer',
        component: CheckOrderManufacturerComponent,
      },
      {
        path: 'order-history-manufacturer/:id',
        component: HistoryOrderDetailComponent,
      },
    ],
  },
  {
    path: 'active-orders',
    canActivate: [AuthGuard],
    canMatch: [
      () => inject(AdminUserGuard).canMatch([Helpers.PermisionManufacturer]),
    ],
    children: [
      {
        path: '',
        component: ManufacturerListComponent,
      },
      {
        path: ':id',
        pathMatch: 'full',
        redirectTo: ':id/check-order-manufacturer',
      },
      {
        path: ':id/check-order-manufacturer',
        component: CheckOrderManufacturerComponent,
      },
    ],
  },

  {
    path: 'pm-orders',
    canActivate: [AuthGuard],
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canDeactivate: [searchTextGuardGuard],
    children: [
      {
        path: '',
        component: OrdiniPMComponent,
      },
      {
        path: ':id',
        children: [
          {
            path: '',
            component: OrderscmDetComponent,
          },
        ],
      },
    ],
  },
  // Legacy /chat path: kept as a redirect for bookmark compatibility.
  // The actual page is now at /messaggi.
  { path: 'chat', redirectTo: 'messaggi', pathMatch: 'full' },

  {
    path: 'fornitore-accounts',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canActivate: [AuthGuard],
    component: FornitoreListAccountsComponent,
  },
  {
    path: 'fornitori-ordini',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canActivate: [AuthGuard],
    component: FornitoriOrdiniListComponent,
    children: [
      {
        path: 'dettagli/:ordineId/:gammaId/:certificatoId',
        component: FornitoriOrdiniDettagliComponent,
      },
      {
        path: 'storico/:ordineId/:gammaId/:certificatoId',
        component: FornitoriOrdiniDettagliComponent,
      },
    ],
  },
  {
    path: 'fornitori-tracciability',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canActivate: [AuthGuard],
    component: TracciabilitaFornitoriComponent,
  },
  {
    path: 'fornitori-tracking/:qr',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canActivate: [AuthGuard],
    component: FornitoriTrackingComponent,
  },
  {
    path: 'partial-orders',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canDeactivate: [searchTextGuardGuard],
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        component: OrdiniParzialiListComponent,
      },
      {
        path: ':id',
        children: [
          {
            path: '',
            component: OrdiniParzialiDettagliComponent,
          },
        ],
      },
    ],
  },

  {
    // New WhatsApp-style chat page — replaces the four legacy paths
    // /messages (list), /messages/:id (detail), /new-msg (compose) and
    // the deprecated /chat. See ChatComponent for the design rationale.
    path: 'messaggi',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canActivate: [AuthGuard],
    component: ChatComponent,
  },

  // Backward-compat redirects: anyone who bookmarked the old paths
  // lands on the new unified page.
  { path: 'messages', redirectTo: 'messaggi', pathMatch: 'full' },
  { path: 'messages/:id', redirectTo: 'messaggi', pathMatch: 'full' },
  { path: 'new-msg', redirectTo: 'messaggi', pathMatch: 'full' },

  {
    path: 'assign-order',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canActivate: [AuthGuard, ScreenLoginGuard],
    canDeactivate: [searchTextGuardGuard],
    children: [
      {
        path: '',
        component: AssegnaOrdineListComponent,
      },
      {
        path: ':id',
        children: [
          {
            path: '',
            component: AssegnaOrdineDettagliComponent,
          },
        ],
      },
    ],
  },

  {
    path: 'products',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        component: GestioneProdottiListComponent,
      },

      {
        path: ':productId',
        children: [
          {
            path: '',
            component: ProductDetailComponent,
          },
        ],
      },
      {
        path: ':family',
        children: [
          {
            path: '',
            component: GestioneProdottiDettagliComponent,
          },
        ],
      },
      {
        path: ':family/:subfamily',
        children: [
          {
            path: '',
            component: GestioneProdottiDettagliComponent,
          },
        ],
      },
      {
        path: ':family/:subfamily/:group',
        children: [
          {
            path: '',
            component: GestioneProdottiDettagliComponent,
          },
        ],
      },
      {
        path: ':family/:subfamily/:group/:subgroup',
        children: [
          {
            path: '',
            component: GestioneProdottiDettagliComponent,
          },
          {
            path: ':id',
            children: [
              {
                path: '',
                component: ProductDetailComponent,
              },
            ],
          },
        ],
      },
    ],
  },

  {
    path: 'products',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        component: GestioneProdottiListComponent,
      },
    ],
  },

  {
    path: 'agents',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        component: AccessoFunzionariListComponent,
      },
    ],
  },

  {
    path: 'agentsorders',
    // canMatch: [() => inject(AdminUserGuard).canMatch([Helpers.PermisionSuperAdmin, Helpers.PermisionAdmin]),],
    canDeactivate: [searchTextGuardGuard],
    children: [
      {
        path: '',
        component: AgenteListComponent,
      },
    ],
  },
  {
    path: 'clientorders',
    // canMatch: [() => inject(AdminUserGuard).canMatch([Helpers.PermisionSuperAdmin, Helpers.PermisionAdmin]),],
    canDeactivate: [searchTextGuardGuard],
    children: [
      {
        path: '',
        component: ClientListComponent,
      },
    ],
  },

  {
    path: 'accounts',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canActivate: [AuthGuard],
    canDeactivate: [searchTextGuardGuard],
    // /accounts/:id (dettagli) and /new-account were children routes in
    // v2.2.x — removed in v2.3.0 with the single-page restyle of the
    // accounts page (modals replaced the per-record routes).
    component: GestioneAccountListComponent,
  },

  // Backward-compat redirects so any old bookmark / external link still
  // lands somewhere useful.
  { path: 'accounts/:id', redirectTo: 'accounts', pathMatch: 'full' },
  { path: 'new-account',   redirectTo: 'accounts', pathMatch: 'full' },
  { path: 'new-account/:id', redirectTo: 'accounts', pathMatch: 'full' },

  {
    path: 'status',
    component: StatusSpedizioniComponent,
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canDeactivate: [searchTextGuardGuard],
    canActivate: [AuthGuard],
  },

  /*{
    path: 'errors',
    component: ErrorComponent,
    canActivate: [AuthGuard],
  },*/

  {
    path: 'history',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canActivate: [AuthGuard],
    canDeactivate: [searchTextGuardGuard],
    children: [
      {
        path: '',
        component: StoricoOrdiniComponent,
      },
      {
        path: ':id',
        children: [
          {
            path: '',
            component: StoricoOrdiniDettagliComponent,
          },
          {
            path: 'errors',
            component: ErrorComponent,
          },
        ],
      },
    ],
  },

  {
    path: 'borderò',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canActivate: [AuthGuard],
    canDeactivate: [searchTextGuardGuard],
    children: [
      {
        path: '',
        component: BorderòComponent,
      },
      {
        path: ':id',
        children: [
          {
            path: '',
            component: BorderòDettagliComponent,
          },
        ],
      },
    ],
  },

  {
    path: 'stats',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    component: StatisticheComponent,
    canActivate: [AuthGuard],
  },

  {
    path: 'firmare',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canDeactivate: [searchTextGuardGuard],
    component: OrdiniDaFirmareComponent,
    canActivate: [AuthGuard],
  },

  {
    path: 'quality',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        component: ControlloQualityComponent,
      },
      /*{
        path: 'dettagli',
        component: ControlloDettagliComponent,
      },*/
    ],
  },

  {
    path: 'settings',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    component: SettingsComponent,
    canActivate: [AuthGuard],
  },

  {
    path: 'prepare-order',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canDeactivate: [searchTextGuardGuard],
    canActivate: [AuthGuard],
    children: [
      {
        path: '',
        component: PreparazioneOrdiniComponent,
      },
      {
        path: ':id',
        children: [
          {
            path: '',
            component: PreparazioneOrdiniDetaggliComponent,
          },
          {
            path: 'collection',
            component: CollectionComponent,
          },
        ],
      },
    ],
  },

  {
    path: 'warehouseorders',
    canMatch: [
      // Auto-login kiosk (query params o sessione hardcoded) — DEVE
      // stare prima di AdminUserGuard, che senza sessione rimanderebbe
      // a /login prima che questa possa essere ricostruita.
      () => inject(ScreenTokenGuard).canMatch(),
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
          Helpers.PermisionScreen,
        ]),
    ],
    canActivate: [AuthGuard],

    children: [
      {
        path: '',
        component: WarehouseOrdersComponent,
      },
      {
        path: ':id',
        children: [
          {
            path: '',
            component: PreparazioneOrdiniDetaggliComponent,
          },
          {
            path: 'collection',
            component: CollectionComponent,
          },
        ],
      },
    ],
  },

  {
    path: 'manufacturer-list',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
          Helpers.PermisionManufacturer,
        ]),
    ],
    // canActivate: [AuthGuard],
    children: [
      {
        path: '',
        component: ManufacturerListComponent,
      },
      {
        path: ':id',
        pathMatch: 'full',
        redirectTo: ':id/check-order-manufacturer',
      },
      {
        path: ':id/check-order-manufacturer',
        component: CheckOrderManufacturerComponent,
      },
    ],
  },

  {
    path: 'storico-parziali',
    canMatch: [
      () =>
        inject(AdminUserGuard).canMatch([
          Helpers.PermisionSuperAdmin,
          Helpers.PermisionAdmin,
        ]),
    ],
    canDeactivate: [searchTextGuardGuard],
    children: [
      {
        path: '',
        component: StoricoParzialiComponent,
      },
    ],
    canActivate: [AuthGuard],
  },

  {
    path: 'login',
    component: LoginComponent,
    canActivate: [AutoLoginGuard],
  },

  {
    path: 'reset-password',
    component: ResetPasswordComponent,
    canActivate: [AutoLoginGuard],
  },

  {
    path: 'fiesta',
    // canMatch: [() => inject(AdminUserGuard).canMatch([Helpers.PermisionSuperAdmin, Helpers.PermisionAdmin]),],
    component: FiestaComponent,
    canActivate: [AutoLoginGuard],
  },

  {
    path: '**',
    pathMatch: 'full',
    component: PageNotFoundComponent,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
